import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Search,
  Filter,
  RefreshCw,
  ExternalLink,
  Edit3,
  CheckCircle2,
  XCircle,
  FileCode
} from 'lucide-react';
import { modificationApi } from '../../services/iso20022Api';
import {
  formatDate,
  getStatusBadgeClass,
  truncate,
  debounce
} from '../../utils/helpers';
import XmlViewerModal from '../../components/iso20022/XmlViewerModal';

const AccountModifications = () => {
  const [modifications, setModifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    status: '',
    search: ''
  });
  const [xmlModalOpen, setXmlModalOpen] = useState(false);
  const [selectedModification, setSelectedModification] = useState(null);

  const loadModifications = async () => {
    try {
      setLoading(true);
      const data = await modificationApi.getModifications(filters);
      console.log('[Load Modifications] Count:', data?.length ?? 0);
      setModifications(data);
    } catch (error) {
      console.error('[Load Modifications] Error:', error);
      setModifications([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadModifications();
  }, [filters]);

  const handleFilterChange = debounce((name, value) => {
    setFilters(prev => ({ ...prev, [name]: value }));
  }, 300);

  const clearFilters = () => {
    setFilters({
      status: '',
      search: ''
    });
  };

  const handleViewXml = (modification) => {
    setSelectedModification(modification);
    setXmlModalOpen(true);
  };

  const getModificationStatusBadgeClass = (status) => {
    if (!status) return 'badge-info';
    
    const statusUpper = status.toUpperCase();
    
    if (statusUpper === 'COMPLETED' || statusUpper === 'SIGNED') {
      return 'badge-success';
    }
    
    if (statusUpper === 'PROCESSING' || statusUpper === 'RECEIVED' || statusUpper === 'SENT') {
      return 'badge-warning';
    }
    
    if (statusUpper === 'REJECTED') {
      return 'badge-danger';
    }
    
    return 'badge-info';
  };

  const getModificationStatusText = (status) => {
    if (!status) return 'Unknown';
    
    const statusMap = {
      'SIGNED': 'Signed',
      'SENT': 'Sent',
      'RECEIVED': 'Received',
      'PROCESSING': 'Processing',
      'COMPLETED': 'Completed',
      'REJECTED': 'Rejected'
    };
    
    return statusMap[status.toUpperCase()] || status;
  };

  const getModificationScopeBadge = (scope) => {
    if (!scope) return null;
    
    const scopeClasses = {
      'ADD': 'bg-green-100 text-green-800',
      'UPDT': 'bg-blue-100 text-blue-800',
      'DEL': 'bg-red-100 text-red-800'
    };
    
    const scopeLabels = {
      'ADD': 'Addition',
      'UPDT': 'Update',
      'DEL': 'Deletion'
    };
    
    const badgeClass = scopeClasses[scope] || 'bg-gray-100 text-gray-800';
    const label = scopeLabels[scope] || scope;
    
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full ${badgeClass}`}>
        {label}
      </span>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Account Modifications</h1>
          <p className="text-gray-600 mt-1">View and manage account modification requests (acmt.003)</p>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={loadModifications}
            disabled={loading}
            className="btn-primary flex items-center space-x-2"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            <span>Refresh</span>
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="card">
        <div className="card-header flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <Filter className="w-5 h-5 text-gray-600" />
            <h2 className="text-lg font-semibold text-gray-900">Filters</h2>
          </div>
          <button
            onClick={clearFilters}
            className="text-sm text-primary-600 hover:text-primary-700 font-medium"
          >
            Clear All
          </button>
        </div>
        <div className="card-body">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {/* Search */}
            <div className="lg:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Search
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by Account ID, Merchant Code, Owner Name..."
                  className="input pl-10"
                  onChange={(e) => handleFilterChange('search', e.target.value)}
                />
              </div>
            </div>

            {/* Status */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Status
              </label>
              <select
                className="select"
                value={filters.status}
                onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
              >
                <option value="">All Statuses</option>
                <option value="COMPLETED">Completed</option>
                <option value="PROCESSING">Processing</option>
                <option value="RECEIVED">Received</option>
                <option value="SIGNED">Signed</option>
                <option value="SENT">Sent</option>
                <option value="REJECTED">Rejected</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Results */}
      <div className="card">
        <div className="card-header">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900">
              Results ({modifications.length})
            </h2>
          </div>
        </div>
        <div className="card-body p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="flex flex-col items-center space-y-4">
                <RefreshCw className="w-8 h-8 text-primary-600 animate-spin" />
                <p className="text-gray-600">Loading modifications...</p>
              </div>
            </div>
          ) : modifications.length === 0 ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <Edit3 className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-900 font-medium mb-2">No modifications found</p>
                <p className="text-gray-600">Try adjusting your filters</p>
              </div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      ID
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Account ID
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Owner Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Modification Reason
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Merchant Code
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Scope
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Signature
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Created
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {modifications.map((mod) => (
                    <tr key={mod.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-mono font-medium text-gray-900">
                          #{mod.id}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-mono text-gray-900">
                          {truncate(mod.accountId, 20)}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900 max-w-xs truncate">
                          {mod.ownerName || 'N/A'}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900 max-w-xs truncate">
                          {mod.modificationReason || 'N/A'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-mono text-gray-600">
                          {mod.merchantCode || 'N/A'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        {getModificationScopeBadge(mod.modificationScope)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        {mod.signatureOk === true ? (
                          <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-success-100" title="Signature valid">
                            <CheckCircle2 className="w-5 h-5 text-success-600" />
                          </span>
                        ) : mod.signatureOk === false ? (
                          <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-danger-100" title="Signature invalid">
                            <XCircle className="w-5 h-5 text-danger-600" />
                          </span>
                        ) : (
                          <span className="text-xs text-gray-400">-</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`badge ${getModificationStatusBadgeClass(mod.status)}`}>
                          {getModificationStatusText(mod.statusLabel || mod.status)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {formatDate(mod.createdAt, 'PPp')}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end space-x-2">
                          {/* Quick XML View - only show if rawXml exists */}
                          {mod.rawXml && (
                            <button
                              onClick={() => handleViewXml(mod)}
                              className="text-blue-600 hover:text-blue-900 p-1 rounded hover:bg-blue-50 transition-colors"
                              title="View XML"
                            >
                              <FileCode className="w-4 h-4" />
                            </button>
                          )}
                          <Link
                            to={`/iso20022/modifications/${mod.id}`}
                            className="text-primary-600 hover:text-primary-900 inline-flex items-center space-x-1"
                          >
                            <span>View</span>
                            <ExternalLink className="w-3 h-3" />
                          </Link>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* XML Viewer Modal */}
      {selectedModification && (
        <XmlViewerModal
          isOpen={xmlModalOpen}
          onClose={() => {
            setXmlModalOpen(false);
            setSelectedModification(null);
          }}
          xmlContent={selectedModification.rawXml}
          fileName={`modification-acmt003-${selectedModification.id}.xml`}
          title={`Account Modification #${selectedModification.id}`}
        />
      )}
    </div>
  );
};

export default AccountModifications;
